import { CommonModule } from '@angular/common';
import { Component, computed, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import {
  combineLatest,
  combineLatestWith,
  map,
  Observable,
  startWith,
  Subject,
  takeUntil,
} from 'rxjs';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { CareAIComponent } from '../../../features/care-ai/care-ai.component';
import { RequestCreationComponent } from '../../../features/requests/components/request-creation/request-creation.component';
import { LanguageSelectorComponent } from '../../../shared/components/language-selector/language-selector.component';
import { logout, selectAccount } from '../../auth/auth.actions';
import {
  AuthState,
  selectActiveAccount,
  selectAllAccounts,
  selectHasRoles,
} from '../../auth/auth.reducer';
import { UserAccount } from '../../auth/user-account.model';
import { Notification } from '../../notifications/notification.model';
import { NotificationService } from '../../notifications/notification.service';
import { LayoutService } from '../layout.service';

type MenuItemExt = MenuItem & {
  isRead?: boolean;
};

@Component({
  selector: 'carehub-header',
  standalone: true,
  imports: [
    FormsModule,
    BadgeModule,
    ButtonModule,
    CommonModule,
    MenuModule,
    RequestCreationComponent,
    LanguageSelectorComponent,
    TooltipModule,
    CareAIComponent,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  private readonly destroying$ = new Subject<void>();
  readonly notifications = signal<Notification[]>([]);
  isRequestDialogVisible: boolean = false;
  isCareAiDialogVisible: boolean = false;

  readonly isSuperAdmin$: Observable<boolean | undefined>;

  readonly notificationMenuItems = computed<MenuItemExt[]>(() => {
    const notifications: Notification[] = this.notifications();

    // locate the first read item
    const firstReadIdx: number = notifications.findIndex(notification => notification.isRead);
    const unread: Notification[] =
      firstReadIdx === -1 ? notifications : notifications.slice(0, firstReadIdx);
    const read: Notification[] =
      firstReadIdx === -1 ? [] : notifications.slice(firstReadIdx, firstReadIdx + 3);

    const toItem: (notification: Notification) => MenuItemExt = notification => ({
      id: String(notification.id),
      label: notification.message,
      url: notification.link,
      isRead: notification.isRead,
    });

    const menuItems: MenuItemExt[] = [];

    if (unread.length) {
      menuItems.push({
        label: $localize`:@@header.notifications.unread:Unread`,
        items: [...unread.map(toItem)],
      });
    }

    if (read.length) {
      if (unread.length) {
        menuItems.push({ separator: true } as MenuItem);
      }
      menuItems.push({
        label: $localize`:@@header.notifications.earlier:Earlier`,
        items: [...read.map(toItem)],
      });
    }

    return menuItems;
  });

  userMenuItems$: Observable<MenuItem[]>;
  activeAccount$: Observable<UserAccount | null>;

  readonly unreadNotificationsCount = computed(() => {
    return this.notifications().filter((notification: Notification) => {
      return !notification.isRead;
    }).length;
  });

  constructor(
    private _layoutService: LayoutService,
    private _notificationService: NotificationService,
    private store: Store<AuthState>
  ) {
    this.activeAccount$ = this.store.select(selectActiveAccount).pipe(takeUntilDestroyed());
    this.isSuperAdmin$ = this.store
      .select(selectHasRoles(['SuperAdmin']))
      .pipe(takeUntilDestroyed());

    this.activeAccount$.pipe(takeUntil(this.destroying$)).subscribe((activeAccount) => {
      const name: string | undefined = activeAccount?.name;

      if (name) {
        this.fetchNotifications(name);
      }
    });

    this.userMenuItems$ = combineLatest([
      this.store.select(selectActiveAccount).pipe(startWith(null)),
      this.store.select(selectAllAccounts).pipe(startWith(null)),
    ]).pipe(
      takeUntilDestroyed(),
      map(([activeAccount, allAccounts]) => {
        if (!allAccounts) {
          return [];
        }

        let accountMenuItems: MenuItem[] = [];
        if (allAccounts.length > 0) {
          accountMenuItems = allAccounts.map((account: UserAccount): MenuItem => {
            return {
              label: account.fullName,
              icon: 'pi pi-user',
              disabled: account.homeAccountId === activeAccount?.homeAccountId,
              command: (): void => {
                this.store.dispatch(selectAccount({ account }));
              },
            };
          });
        }

        const result: MenuItem[] = [
          {
            label: $localize`:@@header.profile.other:Other`,
            items: [
              {
                label: $localize`:@@header.button.addUser:Add User`,
                icon: 'pi pi-user-plus',
                command: () => this.store.dispatch(selectAccount({ account: null })),
              },
              {
                label: $localize`:@@header.button.logout:Logout`,
                icon: 'pi pi-sign-out',
                command: () => this.logout(),
              },
            ],
          },
        ];

        if (accountMenuItems.length > 0) {
          return [
            {
              label: $localize`:@@header.profile.users:Users`,
              items: accountMenuItems,
            },
            { separator: true } as MenuItem,
            ...result,
          ];
        }

        return result;
      })
    );
  }

  ngOnInit(): void {
    this._notificationService
      .listenToCreatedNotifications()
      .pipe(takeUntil(this.destroying$), combineLatestWith(this.activeAccount$))
      .subscribe(([notification, activeAccount]) => {
        if (activeAccount?.name == notification.assignedTo) {
          this.fetchNotifications(activeAccount.name);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }

  toggleSidebar(): void {
    this._layoutService.toggleSidebarState();
  }

  showRequestDialog(): void {
    this.isRequestDialogVisible = true;
  }

  isRead(item: MenuItem): boolean {
    const notification: Notification | undefined = this.notifications().find(
      notification => notification?.id == Number(item.id)
    );
    return notification?.isRead ? true : false;
  }

  /**
   * Open the CareAI dialog
   */
  showCareAiDialog(): void {
    this.isCareAiDialogVisible = true;
  }

  /**
   * Redirects the user to the notification link and marks the notification as read
   *
   * @param notificationId
   */
  async openNotification(notificationId: string): Promise<void> {
    await this._notificationService.markNotificationAsRead(parseInt(notificationId, 10));
    const updatedNotifications: Notification[] = this.notifications().map(
      (notification: Notification) => {
        if (notification.id.toString() === notificationId) {
          return { ...notification, isRead: true };
        }
        return notification;
      }
    );
    this.notifications.set(updatedNotifications);
  }

  logout(): void {
    this.store.dispatch(logout());
  }

  /**
   * Fetches the notifications for the current user
   *
   * @returns void
   */
  private async fetchNotifications(accountName: string): Promise<void> {
    const notifications: Notification[] =
      await this._notificationService.getNotificationsByAssignedTo(accountName);
    this.notifications.set(notifications);
  }
}
